/**
 * V2.0.79 — Architectural PDF preview lightbox extracted from
 * FloorPlan2D.jsx. Renders the embedded PDF iframe + the
 * "Download Clean PDF" / "Export IFC" action buttons.
 *
 * The parent (FloorPlan2D) owns the state machine (`pdfState`,
 * `previewDataUrl`, `legalAccepted`, `legalAcceptanceId`) via the
 * `useExportActions` hook. This component is purely presentational —
 * it takes those values as props and renders the UI. Extracting was
 * safe because there is no local state here, only prop-driven JSX.
 */
import React from "react";
import { toast } from "sonner";
import { X, Loader2, FileCheck, Box } from "lucide-react";
import TosConsentCheckbox from "./TosConsentCheckbox";

function ExportLightbox({
  pdfState,
  previewDataUrl,
  designId,
  legalAccepted,
  setLegalAcceptanceId,
  closeLightbox,
  handleDownloadIFC,
  handleDownloadCleanPDF,
}) {
  if (pdfState !== "previewing" || !previewDataUrl) return null;

  return (
    <div
      data-testid="floor-plan-pdf-lightbox"
      className="fixed inset-0 z-50 grid place-items-center bg-zinc-950/85 backdrop-blur-sm p-4"
      onClick={closeLightbox}
    >
      <div
        className="relative w-full max-w-5xl h-[80vh] bg-zinc-950 border-2 border-brass-700 shadow-[0_20px_80px_rgba(0,0,0,0.7)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={closeLightbox}
          aria-label="Close preview"
          data-testid="lightbox-close"
          className="absolute top-2 right-2 p-1.5 text-zinc-400 hover:text-zinc-100 z-10"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="px-5 py-3 border-b border-zinc-800">
          <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400">
            // Architectural PDF · Preview
          </div>
          <p className="text-zinc-400 text-xs mt-0.5">
            This is the watermarked preview. Click below to unlock and download a clean copy.
          </p>
        </div>
        <iframe
          title="Architectural PDF preview"
          src={previewDataUrl}
          className="flex-1 w-full bg-zinc-800"
          data-testid="lightbox-iframe"
        />
        <div className="px-5 py-3 border-t border-zinc-800 flex items-start justify-between flex-wrap gap-3">
          <TosConsentCheckbox
            surface="lightbox_download"
            designId={designId}
            testIdPrefix="lightbox-legal-accept"
            onAccepted={(id) => setLegalAcceptanceId(id)}
            onRevoked={() => setLegalAcceptanceId(null)}
            onError={() =>
              toast.error("Could not record your acceptance. Please retry.")
            }
            className="max-w-xl"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDownloadIFC}
              disabled={pdfState === "downloading" || !legalAccepted}
              data-testid="lightbox-export-ifc-btn"
              title={
                !legalAccepted
                  ? "Please accept the Terms of Service above to enable downloads."
                  : "Open BIM standard — imports natively into Revit, ArchiCAD, Vectorworks, BricsCAD, BlenderBIM and every major BIM tool."
              }
              className="inline-flex items-center gap-1.5 px-4 py-2 border border-brass-500 text-brass-300 hover:bg-brass-500/10 disabled:opacity-40 disabled:cursor-not-allowed font-mono text-[10px] uppercase tracking-widest"
            >
              <Box className="w-3 h-3" />
              Export IFC
            </button>
            <button
              type="button"
              onClick={handleDownloadCleanPDF}
              disabled={pdfState === "downloading" || !legalAccepted}
              data-testid="lightbox-unlock-btn"
              title={
                !legalAccepted
                  ? "Please accept the Terms of Service above to enable downloads."
                  : undefined
              }
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-mono text-[10px] uppercase tracking-widest"
            >
              {pdfState === "downloading" ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" /> Generating…
                </>
              ) : (
                <>
                  <FileCheck className="w-3 h-3" />
                  Download Clean PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// V2.0.80 — memoized: iframe is expensive to diff and none of our props
// are unstable objects (all primitives + stable callbacks from useCallback
// / hook-returned refs). Skips re-render on unrelated parent state changes.
export default React.memo(ExportLightbox);
